import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipContentProps,
} from 'recharts';
import Card from '../ui/Card';
import { UptimeCheck } from '../../lib/uptime';

interface ResponseTimeChartProps {
  checks: UptimeCheck[];
  height?: number;
  showGrid?: boolean;
  threshold?: number;
}

interface ChartPoint {
  time: string;
  timestamp: number;
  responseTime: number | null;
  isUp: boolean;
  statusCode: number | null;
}

export default function ResponseTimeChart({
  checks,
  height = 300,
  showGrid = true,
  threshold = 500,
}: ResponseTimeChartProps) {
  const chartData = useMemo<ChartPoint[]>(() => {
    if (checks.length === 0) return [];

    // Sort by time and format for chart
    return [...checks]
      .sort((a, b) => new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime())
      .map(check => ({
        time: new Date(check.checked_at).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        timestamp: new Date(check.checked_at).getTime(),
        responseTime: check.is_up ? check.response_time_ms : null,
        isUp: check.is_up,
        statusCode: check.status_code,
      }));
  }, [checks]);

  const stats = useMemo(() => {
    const validTimes = chartData
      .filter(d => d.responseTime !== null)
      .map(d => d.responseTime!);

    if (validTimes.length === 0) return { avg: 0, min: 0, max: 0 };

    return {
      avg: Math.round(validTimes.reduce((a, b) => a + b, 0) / validTimes.length),
      min: Math.min(...validTimes),
      max: Math.max(...validTimes),
    };
  }, [chartData]);

  if (checks.length === 0) {
    return (
      <Card eyebrow="Performance" title="Response Time">
        <div
          style={{
            height: height - 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-faint)',
          }}
        >
          No response time data available
        </div>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }: TooltipContentProps<number, string>) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload as ChartPoint;
    return (
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        <p className="t-eyebrow" style={{ margin: 0 }}>{label}</p>
        {data.isUp ? (
          <>
            <p
              className="t-num"
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--text-md)',
                fontWeight: 600,
                color: 'var(--text-body)',
              }}
            >
              {data.responseTime}ms
            </p>
            <p
              className="t-num"
              style={{ margin: '2px 0 0', fontSize: 'var(--text-xs)', color: 'var(--status-up)' }}
            >
              Status: {data.statusCode}
            </p>
          </>
        ) : (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--text-sm)',
              fontWeight: 500,
              color: 'var(--status-down)',
            }}
          >
            Site was down
          </p>
        )}
      </div>
    );
  };

  return (
    <Card
      eyebrow="Performance"
      title="Response Time"
      actions={
        <div
          className="t-num"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            fontSize: 'var(--text-xs)',
          }}
        >
          <span>
            <span style={{ color: 'var(--text-faint)' }}>Avg </span>
            <span style={{ color: 'var(--text-body)' }}>{stats.avg}ms</span>
          </span>
          <span>
            <span style={{ color: 'var(--text-faint)' }}>Min </span>
            <span style={{ color: 'var(--status-up)' }}>{stats.min}ms</span>
          </span>
          <span>
            <span style={{ color: 'var(--text-faint)' }}>Max </span>
            <span style={{ color: 'var(--status-warn)' }}>{stats.max}ms</span>
          </span>
        </div>
      }
    >
      <ResponsiveContainer width="100%" height={height - 60}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
          )}
          <XAxis
            dataKey="time"
            tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
            axisLine={{ stroke: 'var(--border-hairline)' }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          <YAxis
            tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
            axisLine={{ stroke: 'var(--border-hairline)' }}
            tickLine={false}
            tickFormatter={value => `${value}ms`}
            width={60}
          />
          <Tooltip content={CustomTooltip} />
          <ReferenceLine
            y={threshold}
            stroke="var(--status-warn)"
            strokeDasharray="5 5"
            label={{
              value: `${threshold}ms threshold`,
              fill: 'var(--status-warn)',
              fontSize: 10,
              // Inside the plot: 'right' drew past the 20px margin and got cut
              // off (and widened the SVG past a phone screen).
              position: 'insideTopRight',
            }}
          />
          <Line
            type="monotone"
            dataKey="responseTime"
            stroke="var(--chart-1)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: 'var(--chart-1)' }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}
